import IncludedMembershipJoin from '../../components/join/IncludedMembershipJoin';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { JoinProvider, useJoin } from '../../components/join/joinContext';
import JoinFlowShell from '../../components/join/JoinFlowShell';
import JoinMembershipType from '../../components/join/joinMembershipType';
import JoinForm from '../../components/join/joinForm';
import JoinPayment from '../../components/join/joinPayment';
import JoinLearnToRow from '../../components/join/joinLearnToRow';
import JoinLearnToSail from '../../components/join/joinLearnToSail';
import JoinWaiting from '../../components/join/joinWaiting';
import JoinJoined from '../../components/join/joinJoined';
import ErrorMessage from '../../components/forms/formComponents/ErrorMessage';
import { db } from '../../firebase-client';
import { buildRequiredTagGroups, getPricingTagNames } from '../../utils/requiredTags';
import { getBespokeModuleSupport } from '../../components/teams/utils/teamMetadata';

export function JoinFlow() {
  const {
    status,
    setStatus,
    error,
    team,
    teamId,
    user,
    hasPendingMemberDoc,
    hasPaid,
    questions,
    requiredJoinDocuments,
    stripeSetup,
    isPrelaunchCommitteeMode,
  } = useJoin();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [releasedPlaceChecked, setReleasedPlaceChecked] = useState(false);
  const [onlineRouteSetup, setOnlineRouteSetup] = useState(null);
  const restartAppliedRef = useRef(false);
  const adminEntryRef = useRef(null);
  const demoRequested = useMemo(() => {
    const raw = String(searchParams.get('demo') || '').trim().toLowerCase();
    return raw === '1' || raw === 'true' || raw === 'yes';
  }, [searchParams]);
  const manageRequested = useMemo(() => {
    const raw = String(searchParams.get('manage') || '').trim().toLowerCase();
    return raw === '1' || raw === 'true' || raw === 'yes';
  }, [searchParams]);
  const restartMembershipRequested = searchParams.get('restartMembership') === '1';
  const learnToRowRequested = useMemo(() => {
    const raw = String(searchParams.get('joinMode') || '').trim().toLowerCase();
    return raw === 'learn-to-row';
  }, [searchParams]);
  const learnToSailRequested = useMemo(() => {
    const raw = String(searchParams.get('joinMode') || '').trim().toLowerCase();
    return raw === 'learn-to-sail';
  }, [searchParams]);
  const courseSignupRequested = learnToRowRequested || learnToSailRequested;
  const bespokeModuleSupport = useMemo(
    () => getBespokeModuleSupport(team?.teamMetadata || {}),
    [team?.teamMetadata]
  );
  const requestedCourseAvailable = learnToRowRequested
    ? bespokeModuleSupport.rowing && team?.modules?.rowing?.learnToRow?.enabled === true
    : learnToSailRequested
      ? bespokeModuleSupport.sailing && team?.modules?.sailing?.learnToSail?.enabled === true
      : true;
  const isPermissionDeniedError = typeof error === 'string'
    && /permission-denied|insufficient permissions/i.test(error);
  const nextPath = `${location.pathname}${location.search || ''}`;
  const encodedNextPath = encodeURIComponent(nextPath);
  const teamAdmins = Array.isArray(team?.admins) ? team.admins : [];
  const isAdmin = Boolean(user && teamAdmins.includes(user.uid));
  const isAdminDemo = demoRequested && isAdmin;
  const isAdminManagingMembership = manageRequested && isAdmin;
  const isPrelaunchCommittee = isPrelaunchCommitteeMode;
  const requiresMembershipTypeStep = true;
  const hasJoinFormQuestions = Array.isArray(questions)
    && questions.some((question) => !['pricingTags', 'pricingTag'].includes(question?.id));
  const hasRequiredJoinDocuments = Array.isArray(requiredJoinDocuments) && requiredJoinDocuments.length > 0;
  const requiredNonPricingGroups = useMemo(() => buildRequiredTagGroups(team?.tags || [], {
    pricingTagNames: getPricingTagNames(stripeSetup || {})
  }).filter(group => !group.isPricingGroup), [stripeSetup, team?.tags]);
  const requiresJoinFormStep = team?.availability?.joinable === 'On Approval' || hasJoinFormQuestions || hasRequiredJoinDocuments || requiredNonPricingGroups.length > 0;
  const demoStartStatus = requiresMembershipTypeStep
    ? 'membership_type'
    : 'form';
  const manageStartStatus = requiresMembershipTypeStep
    ? 'membership_type'
    : (requiresJoinFormStep ? 'form' : 'payment');

  useEffect(() => {
    if (courseSignupRequested || isAdminDemo || !teamId || !user?.uid) {
      setReleasedPlaceChecked(true);
      return;
    }
    let cancelled = false;
    const loadReleasedPlace = async () => {
      try {
        const settingsSnap = await getDoc(doc(db, 'users', user.uid, 'teamSettings', teamId)).catch(() => null);
        if (cancelled) return;
        const settings = settingsSnap?.exists() ? settingsSnap.data() || {} : {};
        const routeChange = settings.pendingMembershipCollectionRouteChange;
        if (manageRequested && team?.membershipHandling?.mode === 'hybrid'
          && routeChange?.targetRoute === 'stripe' && routeChange.status === 'approved_pending_payment_setup') {
          setOnlineRouteSetup(settings);
          return;
        }
        setOnlineRouteSetup(null);
        if (isAdminManagingMembership) return;
        const releasedPlaces = [
          ...(settingsSnap?.exists() && settingsSnap.data()?.learnToRowReleasedPlaces && typeof settingsSnap.data().learnToRowReleasedPlaces === 'object'
            ? Object.values(settingsSnap.data().learnToRowReleasedPlaces).map((entry) => ({ ...entry, joinMode: 'learn-to-row' }))
            : []),
          ...(settingsSnap?.exists() && settingsSnap.data()?.learnToSailReleasedPlaces && typeof settingsSnap.data().learnToSailReleasedPlaces === 'object'
            ? Object.values(settingsSnap.data().learnToSailReleasedPlaces).map((entry) => ({ ...entry, joinMode: 'learn-to-sail' }))
            : []),
        ];
        const releasedPlace = releasedPlaces
          .map((entry) => ({
            courseId: typeof entry?.courseId === 'string' ? entry.courseId.trim() : '',
            targetUserId: typeof entry?.targetUserId === 'string' ? entry.targetUserId.trim() : '',
            joinMode: entry?.joinMode === 'learn-to-sail' ? 'learn-to-sail' : 'learn-to-row',
            releasedMillis: typeof entry?.releasedAt?.toMillis === 'function'
              ? entry.releasedAt.toMillis()
              : new Date(entry?.releasedAt || 0).getTime(),
          }))
          .filter((entry) => entry.courseId && entry.targetUserId)
          .sort((left, right) => Number(right.releasedMillis || 0) - Number(left.releasedMillis || 0))[0];
        if (releasedPlace?.courseId && releasedPlace?.targetUserId) {
          const nextParams = new URLSearchParams(location.search);
          nextParams.set('joinMode', releasedPlace.joinMode);
          nextParams.set('courseId', releasedPlace.courseId);
          nextParams.set('targetUserId', releasedPlace.targetUserId);
          navigate(`/join/${teamId}?${nextParams.toString()}`, { replace: true });
          return;
        }
      } finally {
        if (!cancelled) setReleasedPlaceChecked(true);
      }
    };
    loadReleasedPlace();
    return () => {
      cancelled = true;
    };
  }, [courseSignupRequested, isAdminDemo, isAdminManagingMembership, manageRequested, location.search, navigate, teamId, team?.membershipHandling?.mode, user?.uid]);

  useEffect(() => {
    if (onlineRouteSetup && status !== 'payment' && status !== 'loading') setStatus('payment');
  }, [onlineRouteSetup, setStatus, status]);

  useEffect(() => {
    if (!isAdminDemo) return;
    if (status === 'joined' || status === 'waiting' || status === 'waitlisted' || status === 'payment') {
      setStatus(demoStartStatus);
    }
  }, [isAdminDemo, status, setStatus, demoStartStatus]);

  useEffect(() => {
    if (!isAdminManagingMembership || onlineRouteSetup || status === 'loading') return;
    const entryKey = JSON.stringify([teamId, user?.uid, location.pathname, location.search]);
    if (adminEntryRef.current === entryKey) return;
    adminEntryRef.current = entryKey;
    if (status === 'joined' || status === 'waiting') {
      setStatus(manageStartStatus);
    }
  }, [isAdminManagingMembership, onlineRouteSetup, status, setStatus, manageStartStatus, teamId, user?.uid, location.pathname, location.search]);

  useEffect(() => {
    if (!isPrelaunchCommittee || !restartMembershipRequested || restartAppliedRef.current || status === 'loading') return;
    restartAppliedRef.current = true;
    setStatus('membership_type');
  }, [isPrelaunchCommittee, restartMembershipRequested, setStatus, status]);

  if (isPermissionDeniedError && !user) {
    return (
      <div className="form-page-container py-12 px-6 text-center">
        <h2 className="text-2xl font-semibold mb-2">Log in to continue</h2>
        <p className="text-lg mb-6">
          This join link is available once you&apos;re logged in.
        </p>
        <div className="flex justify-center gap-3 flex-wrap">
          <Link className="button" to={`/login?next=${encodedNextPath}`}>
            Log in
          </Link>
          <Link className="button button-secondary" to={`/register?next=${encodedNextPath}`}>
            Sign up
          </Link>
        </div>
      </div>
    );
  }

  if (error) return <ErrorMessage message={error} />;
  if (status === 'loading') return <p>Loading team info...</p>;
  if (!courseSignupRequested && !releasedPlaceChecked) return <p>Loading team info...</p>;

  if (courseSignupRequested && !requestedCourseAvailable) {
    return (
      <div className="form-page-container py-12 px-6 text-center">
        <h2 className="text-2xl font-semibold mb-2">Course signup unavailable</h2>
        <p className="text-lg mb-6">This course module is not available for {team?.name || 'this club'}.</p>
        <Link className="button button-secondary" to={`/join/${teamId}`}>View membership options</Link>
      </div>
    );
  }

  if (!courseSignupRequested && !isAdminDemo && !isAdminManagingMembership && !onlineRouteSetup && (hasPaid || status === 'joined')) {
    return (
      <div className="card">
        <h2 className="text-2xl font-semibold text-center mb-2">Join {team?.name}</h2>
        <JoinJoined />
      </div>
    );
  }

  const { name, availability = {} } = team || {};
  if (team?.billingAccess?.locked === true) {
    return <div className="form-page-container py-12 px-6 text-center">
      <h2 className="text-2xl font-semibold mb-2">Club temporarily unavailable</h2>
      <p className="text-lg">This club is resolving an account issue. Joining will reopen when access is restored.</p>
    </div>;
  }
  const isJoinable =
    availability.live &&
    (availability.joinable === 'Open' ||
      availability.joinable === 'On Approval' ||
      hasPendingMemberDoc);

  if (!courseSignupRequested && !isAdminDemo && !isAdminManagingMembership && !onlineRouteSetup && !isJoinable) {
    return (
      <div className="form-page-container py-12 px-6 text-center">
        <h2 className="text-2xl font-semibold mb-2">Sorry!</h2>
        <p className="text-lg">{name} is not accepting new members at this time.</p>
      </div>
    );
  }

  return (
    <JoinFlowShell
      learnToRowRequested={learnToRowRequested}
      learnToSailRequested={learnToSailRequested}
      isAdminDemo={isAdminDemo}
      isAdmin={isAdmin}
      isAdminManagingMembership={isAdminManagingMembership}
    >
      {isPrelaunchCommittee && (
        <div className="card" role="note">
          <strong>Pre-launch committee setup</strong>
          <p>Complete the normal membership details now. If the club uses Member Nest payments, your card will only be authorised and will not be charged until the club’s first launch attempt.</p>
        </div>
      )}
      {(() => {
        if (learnToRowRequested) {
          return <JoinLearnToRow />;
        }
        if (learnToSailRequested) {
          return <JoinLearnToSail />;
        }
        switch (status) {
          case 'form':
            return <JoinForm />;
          case 'membership_type':
            return <JoinMembershipType />;
          case 'payment':
            return <JoinPayment onlineRouteSetup={onlineRouteSetup} />;
          case 'waiting':
          case 'waitlisted':
            return <JoinWaiting />;
          case 'joined':
            return <JoinJoined />;
          default:
            return <p>Unknown join status</p>;
        }
      })()}
    </JoinFlowShell>
  );
}

export default function JoinTeamPageWrapper() {
  const { teamId } = useParams();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isDemoMode = useMemo(() => {
    const raw = String(searchParams.get('demo') || '').trim().toLowerCase();
    return raw === '1' || raw === 'true' || raw === 'yes';
  }, [searchParams]);
  const isLearnToRowMode = useMemo(() => {
    const raw = String(searchParams.get('joinMode') || '').trim().toLowerCase();
    return raw === 'learn-to-row';
  }, [searchParams]);
  const isLearnToSailMode = useMemo(() => {
    const raw = String(searchParams.get('joinMode') || '').trim().toLowerCase();
    return raw === 'learn-to-sail';
  }, [searchParams]);
  const isPrelaunchCommitteeMode = searchParams.get('prelaunchCommittee') === '1';

  useEffect(() => {
    document.documentElement.classList.add('join-flow-route');
    document.body.classList.add('join-flow-route');

    return () => {
      document.documentElement.classList.remove('join-flow-route');
      document.body.classList.remove('join-flow-route');
    };
  }, []);

  if (searchParams.get('included')) return <IncludedMembershipJoin teamId={teamId} invitationKey={searchParams.get('included')} />;

  return (
    <JoinProvider teamId={teamId} isDemoMode={isDemoMode} isLearnToRowMode={isLearnToRowMode || isLearnToSailMode} isPrelaunchCommitteeMode={isPrelaunchCommitteeMode}>
      <div className="join-flow-page">
        <JoinFlow />
      </div>
    </JoinProvider>
  );
}
